import axios from "axios"
import { NextResponse } from "next/server"
import { z } from "zod"
import type {
  AccountOrderDetail,
  AccountOrderDetailResponse,
  OrderAddress,
  OrderTotalLine,
} from "@/api/account-order-detail"
import { clearSession, getSession } from "@/services/auth-service"
import {
  getWordPressOrderDetail,
  type WordPressOrderAddress,
} from "@/services/wordpress-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

const paramsSchema = z.object({
  number: z.string().regex(/^\d+$/, "Order number must be numeric"),
})

function formatCurrency(value: number, currency: string): string {
  const currencyCode = currency?.trim() ? currency.toUpperCase() : "BRL"
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currencyCode,
  }).format(value)
}

function transformAddress(wpAddress: WordPressOrderAddress): OrderAddress {
  return {
    firstName: wpAddress.first_name,
    lastName: wpAddress.last_name,
    company: wpAddress.company,
    address1: wpAddress.address_1,
    address2: wpAddress.address_2,
    city: wpAddress.city,
    state: wpAddress.state,
    postcode: wpAddress.postcode,
    country: wpAddress.country,
    phone: wpAddress.phone,
    email: wpAddress.email,
  }
}

export async function GET(
  _: Request,
  context: { params: Promise<{ number: string }> }
) {
  try {
    const params = await context.params
    const validation = paramsSchema.safeParse(params)

    if (!validation.success) {
      return NextResponse.json<AccountOrderDetailResponse>(
        {
          ok: false,
          code: "INVALID_PARAMS",
          message:
            validation.error.issues[0]?.message ?? "Invalid order number",
        },
        { status: 422, headers: NO_CACHE_HEADERS }
      )
    }

    const { number: orderNumber } = validation.data

    const session = await getSession()
    const sessionToken = session?.wordpressSessionCookie?.trim()

    if (!sessionToken) {
      return NextResponse.json<AccountOrderDetailResponse>(
        {
          ok: false,
          code: "UNAUTHORIZED",
          message: "Authentication required",
        },
        { status: 401, headers: NO_CACHE_HEADERS }
      )
    }

    const wpOrder = await getWordPressOrderDetail(sessionToken, orderNumber)

    const totals: OrderTotalLine[] = [
      {
        label: "Subtotal",
        value: formatCurrency(Number(wpOrder.subtotal), wpOrder.currency),
      },
    ]

    if (Number(wpOrder.discount_total) > 0) {
      totals.push({
        label: "Desconto",
        value: formatCurrency(Number(wpOrder.discount_total), wpOrder.currency),
      })
    }

    if (Number(wpOrder.shipping_total) > 0) {
      totals.push({
        label: "Frete",
        value: formatCurrency(Number(wpOrder.shipping_total), wpOrder.currency),
      })
    }

    if (Number(wpOrder.total_tax) > 0) {
      totals.push({
        label: "Impostos",
        value: formatCurrency(Number(wpOrder.total_tax), wpOrder.currency),
      })
    }

    totals.push({
      label: "Total",
      value: formatCurrency(Number(wpOrder.total), wpOrder.currency),
    })

    const order: AccountOrderDetail = {
      id: Number(wpOrder.id),
      number: wpOrder.number,
      status: wpOrder.status,
      dateCreated: wpOrder.date_created,
      currency: wpOrder.currency,

      items: wpOrder.line_items.map((item) => ({
        id: item.id,
        name: item.name,
        quantity: Number(item.quantity),
        subtotal: Number(item.subtotal),
        total: Number(item.total),
        sku: item.sku,
        image: item.image?.src,
        productId: item.product_id,
        productPermalink: item.product_permalink,
        variationAttributes: item.meta_data
          ?.filter((meta) => meta.display_key && meta.display_value)
          .map((meta) => ({
            name: meta.display_key ?? "",
            value: meta.display_value ?? "",
          })),
      })),

      totals,

      billingAddress: transformAddress(wpOrder.billing),
      shippingAddress: wpOrder.needs_shipping_address
        ? transformAddress(wpOrder.shipping)
        : undefined,
      needsShipping: wpOrder.needs_shipping_address,

      customerNote: wpOrder.customer_note,
      orderNotes:
        wpOrder.customer_order_notes?.map((note) => ({
          id: note.id,
          date: note.date_created,
          content: note.note,
        })) ?? [],
    }

    return NextResponse.json<AccountOrderDetailResponse>(
      { ok: true, order },
      {
        headers: NO_CACHE_HEADERS,
      }
    )
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        return NextResponse.json<AccountOrderDetailResponse>(
          {
            ok: false,
            code: "ORDER_NOT_FOUND",
            message: "Order not found or access denied",
          },
          { status: 404, headers: NO_CACHE_HEADERS }
        )
      }

      if (error.response?.status === 401 || error.response?.status === 403) {
        await clearSession()

        return NextResponse.json<AccountOrderDetailResponse>(
          {
            ok: false,
            code: "UNAUTHORIZED",
            message: "Invalid or expired session",
          },
          { status: 401, headers: NO_CACHE_HEADERS }
        )
      }
    }

    return NextResponse.json<AccountOrderDetailResponse>(
      {
        ok: false,
        code: "BACKEND_ERROR",
        message: "Failed to fetch order details",
      },
      { status: 502, headers: NO_CACHE_HEADERS }
    )
  }
}
